import { withTryToInvokeTrackingEvent } from '@utils/exponea-logging';

import { RecommendationPlacement } from './recommendationPlacement';

export type RecommendationAttributesInput = {
  provider?: string | null;
  recommendationId?: string | null;
  engineName?: string | null;
};

export const normalizeRecommendationAttributes = (
  attributes?: RecommendationAttributesInput
) => ({
  provider:
    (attributes?.provider as RecommendationTrackingProvider | null) ?? null,
  recommendationId: attributes?.recommendationId ?? null,
  engineName: attributes?.engineName ?? null,
});

type NormalizedAttributes = ReturnType<
  typeof normalizeRecommendationAttributes
>;

type BatchPayload = NormalizedAttributes & {
  placement: RecommendationPlacement;
  productCodes: string[];
  productMasterCodes: string[];
};

export const RECOMMENDATION_CLICK_TYPE = {
  ctaButton: 'cta_button',
} as const;

export type RecommendationClickType =
  (typeof RECOMMENDATION_CLICK_TYPE)[keyof typeof RECOMMENDATION_CLICK_TYPE];

type ClickPayload = NormalizedAttributes & {
  placement: RecommendationPlacement;
  productCode: string;
  productMasterCode: string;
  /** Omitted for a click on the product itself. */
  clickType?: RecommendationClickType;
};

type FailedPayload = NormalizedAttributes & {
  placement: RecommendationPlacement;
  message: string;
};

type WithProvider<T> = T & { provider: RecommendationTrackingProvider };

const emit = (payload: RecommendationTrackEventPayload) =>
  window.trackingEvents?.TrackEvent(payload);

const trackEvent = withTryToInvokeTrackingEvent(
  'TrackEvent',
  (payload: RecommendationTrackEventPayload) => emit(payload)
);

const hasProvider = <T extends NormalizedAttributes>(
  payload: T
): payload is WithProvider<T> => payload.provider !== null;

export const trackRecommendationShow = (payload: BatchPayload) => {
  if (!hasProvider(payload)) return;
  trackEvent({ type: 'RecommendationShow', ...payload });
};

export const trackRecommendationInView = (payload: BatchPayload) => {
  if (!hasProvider(payload)) return;
  trackEvent({ type: 'RecommendationInView', ...payload });
};

export const trackRecommendationClick = ({
  clickType,
  ...payload
}: ClickPayload) => {
  if (!hasProvider(payload)) return;
  trackEvent({
    type: 'RecommendationClick',
    ...payload,
    ...(clickType && { properties: { click_type: clickType } }),
  });
};

// Unlike Show/InView/Click, a failed request is worth reporting even when the
// response carried no provider headers - the metadata is propagated only
// "where technically available" (US #1034207).
export const trackRecommendationFailed = (payload: FailedPayload) => {
  trackEvent({ type: 'RecommendationFailed', ...payload });
};
